import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { GET_BANNERS_QUERY, GET_MAIN_CATEGORIES, GET_PRODUCTS } from '../../apollo/queries';
import { useLoading } from '../../contexts/LoadingContext';
import '../css/HomePage.css';
import '../../components/css/ProductModal.css';
import ProductModal from '../../components/ProductModal';
import ImageWithPlaceholder from '../../components/ImageWithPlaceholder';

const ProductCard = ({ product, onCardClick }) => {
    // 실제 제품 데이터 구조에 맞게 수정
    const getProductImage = () => {
        if (!product.images) {
            return null; // null을 반환하면 ImageWithPlaceholder가 SVG 표시
        }
        
        try {
            if (typeof product.images === 'string') {
                const cleanedImages = product.images.trim();
                
                // 빈 문자열이나 빈 배열 체크
                if (cleanedImages === '' || cleanedImages === '[]') {
                    return null;
                }
                
                // JSON 배열인 경우 파싱
                if (cleanedImages.startsWith('[')) {
                    const parsedImages = JSON.parse(cleanedImages);
                    if (Array.isArray(parsedImages) && parsedImages.length > 0) {
                        return parsedImages[0];
                    }
                    return null;
                }
                
                // 단일 URL 문자열인 경우
                if (cleanedImages.includes('http') || cleanedImages.includes('/')) {
                    return cleanedImages;
                }
            } else if (Array.isArray(product.images) && product.images.length > 0) {
                return product.images[0];
            }
        } catch (e) {
            console.error('이미지 파싱 에러:', e, 'images:', product.images);
        }
        
        return null;
    };

    return (
        <div className="product-card" onClick={() => onCardClick(product)}>
            <div className="product-image-container">
                <ImageWithPlaceholder src={getProductImage()} alt={product.name} />
            </div>
            <div className="product-info">
                <p className="product-code">{product.productCode || product.id || ''}</p>
                <h4 className="product-title">{product.name}</h4>
                <p className="product-subtitle">{product.description || product.specifications || ''}</p>
            </div>
        </div>
    );
};

// 메인 탭 컴포넌트
const MainTabSection = ({ categories, activeTab, setActiveTab, onProductClick }) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [itemsPerPage, setItemsPerPage] = useState(4);
    
    // 모바일 반응형 처리
    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth <= 768) {
                setItemsPerPage(2);
            } else {
                setItemsPerPage(4);
            }
        };
        
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);
    
    // 탭 변경 시 인덱스 리셋 - Hook은 early return 전에 위치해야 함
    useEffect(() => {
        setCurrentIndex(0);
    }, [activeTab]);
    
    if (!categories || categories.length === 0) {
        return null;
    }

    const activeCategory = categories[activeTab] || {};
    const products = activeCategory.products || [];
    const totalProducts = products.length;
    const maxIndex = Math.max(0, totalProducts - itemsPerPage);

    const handlePrevSlide = () => {
        setCurrentIndex(prev => Math.max(0, prev - itemsPerPage));
    };

    const handleNextSlide = () => {
        setCurrentIndex(prev => Math.min(maxIndex, prev + itemsPerPage));
    };

    const currentProducts = products.slice(currentIndex, currentIndex + itemsPerPage);
    const currentPage = Math.floor(currentIndex / itemsPerPage) + 1;
    const totalPages = Math.ceil(totalProducts / itemsPerPage);

    return (
        <div className="tabbed-section">
            <div className="tabs">
                {categories.map((category, index) => (
                    <div 
                        key={index}
                        className={`tab-item ${activeTab === index ? 'active' : ''}`}
                        onClick={() => setActiveTab(index)}
                    >
                        <div className="tab-text">{category.name}</div>
                        <div className="tab-border"></div>
                    </div>
                ))}
            </div>
            <div className="tab-content">
                <div className="product-grid-wrapper">
                    <button 
                        className={`carousel-arrow left ${currentIndex === 0 ? 'disabled' : ''}`}
                        onClick={handlePrevSlide}
                        disabled={currentIndex === 0}
                        aria-label="이전"
                    >
                        <img src="/polygon-170.svg" alt="이전" />
                    </button>
                    <div className="product-grid">
                        {currentProducts.length > 0 ? (
                            currentProducts.map((product, index) => (
                                <ProductCard key={product.id || index} product={product} onCardClick={onProductClick} />
                            ))
                        ) : (
                            <div style={{ padding: '50px', textAlign: 'center', color: '#999' }}>
                                해당 카테고리에 등록된 제품이 없습니다.
                            </div>
                        )}
                    </div>
                    <button 
                        className={`carousel-arrow right ${currentIndex >= maxIndex ? 'disabled' : ''}`}
                        onClick={handleNextSlide}
                        disabled={currentIndex >= maxIndex}
                        aria-label="다음"
                    >
                        <img src="/polygon-160.svg" alt="다음" />
                    </button>
                </div>
                <div className="pagination">
                    <span>{totalProducts > 0 ? `${currentPage} / ${totalPages}` : '0 / 0'}</span>
                </div>
            </div>
        </div>
    );
};

// 카테고리별 제품 섹션 컴포넌트
const CategoryProductSection = ({ category, onProductClick }) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [itemsPerPage, setItemsPerPage] = useState(4);
    
    // 모바일 반응형 처리
    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth <= 768) {
                setItemsPerPage(2);
            } else {
                setItemsPerPage(4);
            }
        };
        
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);
    
    if (!category || !category.products || category.products.length === 0) {
        return null;
    }

    const products = category.products;
    const totalProducts = products.length;
    const maxIndex = Math.max(0, totalProducts - itemsPerPage);

    const handlePrevSlide = () => {
        setCurrentIndex(prev => Math.max(0, prev - itemsPerPage));
    };

    const handleNextSlide = () => {
        setCurrentIndex(prev => Math.min(maxIndex, prev + itemsPerPage));
    };

    const currentProducts = products.slice(currentIndex, currentIndex + itemsPerPage);
    const currentPage = Math.floor(currentIndex / itemsPerPage) + 1;
    const totalPages = Math.ceil(totalProducts / itemsPerPage);

    return (
        <div className="product-grid-wrapper product-grid-wrapper--with-header">
            <div className="grid-tit">
                <div className="div">{category.name}</div>
                <div className="div2">{category.description || `${category.products.length}개 제품`}</div>
            </div>
            <div className="product-grid-slides-row">
                <button 
                    className={`carousel-arrow left ${currentIndex === 0 ? 'disabled' : ''}`}
                    onClick={handlePrevSlide}
                    disabled={currentIndex === 0}
                    aria-label="이전"
                >
                    <img src="/polygon-170.svg" alt="Previous" />
                </button>
                <div className="product-grid">
                    {currentProducts.map((product) => (
                        <ProductCard key={product.id} product={product} onCardClick={onProductClick} />
                    ))}
                </div>
                <button 
                    className={`carousel-arrow right ${currentIndex >= maxIndex ? 'disabled' : ''}`}
                    onClick={handleNextSlide}
                    disabled={currentIndex >= maxIndex}
                    aria-label="다음"
                >
                    <img src="/polygon-160.svg" alt="Next" />
                </button>
            </div>
            <div className="pagination">
                <span>{currentPage} / {totalPages}</span>
            </div>
        </div>
    );
};

const HomePageContent = () => {
    const [modalProduct, setModalProduct] = useState(null);
    const [activeTab, setActiveTab] = useState(0);
    const { startLoading, stopLoading, updateMessage } = useLoading();

    // GraphQL 쿼리들
    const { data: bannerData, loading: bannerLoading, error: bannerError } = useQuery(GET_BANNERS_QUERY, {
        fetchPolicy: 'cache-and-network'
    });

    const { data: categoriesData, loading: categoriesLoading, error: categoriesError } = useQuery(GET_MAIN_CATEGORIES, {
        fetchPolicy: 'cache-and-network'
    });

    const { data: productsData, loading: productsLoading, error: productsError } = useQuery(GET_PRODUCTS, {
        variables: { isActive: true, limit: 1000 },
        fetchPolicy: 'cache-and-network'
    });

    const handleProductClick = (product) => {
        setModalProduct(product);
    };

    const handleCloseModal = () => {
        setModalProduct(null);
    };

    // 로딩 상태 관리
    useEffect(() => {
        const isAnyLoading = bannerLoading || categoriesLoading || productsLoading;
        
        if (isAnyLoading) {
            if (bannerLoading) {
                startLoading('배너를 불러오는 중...');
            } else if (categoriesLoading) {
                updateMessage('카테고리를 불러오는 중...');
            } else if (productsLoading) {
                updateMessage('제품을 불러오는 중...');
            }
        } else {
            stopLoading();
        }
    }, [bannerLoading, categoriesLoading, productsLoading, startLoading, stopLoading, updateMessage]);

    // 배너 데이터 처리
    const getBannerByPosition = (position) => {
        if (!bannerData?.banners) return null;
        return bannerData.banners.find(banner => 
            banner.position === position && banner.isVisible
        );
    };

    const handleBannerClick = (banner) => {
        if (banner?.linkUrl) {
            window.open(banner.linkUrl, '_blank');
        }
    };

    // 하단 배너 가져오기
    const bottomBanner = getBannerByPosition('하단');

    // 카테고리와 제품 데이터 처리
    const categories = categoriesData?.mainCategories || [];
    const products = productsData?.products?.products || []; // 페이지네이션 구조에 맞게 수정

    // 카테고리별로 제품 그룹화
    const categoriesWithProducts = categories.map(category => ({
        ...category,
        products: products.filter(product => product.mainCategoryId === category.id)
    }));

    // 1-6번째 카테고리 선택 (최대 6개)
    const displayCategories = categoriesWithProducts.slice(0, 6);

    // 탭 순서: 모선부스바 → 마그네트 부스바 → 분전반 부스바 → 기타자재
    const TAB_ORDER = ['모선부스바', '마그네트 부스바', '분전반 부스바', '기타자재'];
    const tabCategoriesOrdered = [...displayCategories.slice(2, 6)].sort((a, b) => {
        const ai = TAB_ORDER.indexOf(a.name);
        const bi = TAB_ORDER.indexOf(b.name);
        if (ai === -1 && bi === -1) return 0;
        if (ai === -1) return 1;
        if (bi === -1) return -1;
        return ai - bi;
    });

    // 로딩 상태
    if (categoriesLoading || productsLoading) {
        return (
            <div className="homepage">
                <div style={{ textAlign: 'center', padding: '100px' }}>
                    데이터를 불러오는 중...
                </div>
            </div>
        );
    }

    return (
        <div className="homepage">
            <main className="main-content">
                {modalProduct && <ProductModal key="product-modal" product={modalProduct} onClose={handleCloseModal} />}
                
                <div className="hero-section">
                    <div className="hero-main">
                        {(() => {
                            const firstBanner = getBannerByPosition('첫번째');
                            return firstBanner ? (
                                <img 
                                    src={firstBanner.imageUrl} 
                                    alt="첫번째 배너"
                                    style={{ 
                                        width: '100%', 
                                        height: '100%', 
                                        objectFit: 'cover',
                                        cursor: firstBanner.linkUrl ? 'pointer' : 'default'
                                    }}
                                    onClick={() => handleBannerClick(firstBanner)}
                                />
                            ) : (
                                <ImageWithPlaceholder src={null} alt="메인 배너" />
                            );
                        })()}
                    </div>
                    <div className="hero-side">
                        <div className={`hero-side-item top-left ${getBannerByPosition('두번째') ? 'has-banner-image' : ''}`}>
                            {(() => {
                                const secondBanner = getBannerByPosition('두번째');
                                return secondBanner ? (
                                    <img 
                                        src={secondBanner.imageUrl} 
                                        alt="두번째 배너"
                                        style={{ 
                                            width: '100%', 
                                            height: '100%', 
                                            objectFit: 'contain',
                                            cursor: secondBanner.linkUrl ? 'pointer' : 'default'
                                        }}
                                        onClick={() => handleBannerClick(secondBanner)}
                                    />
                                ) : (
                                    <>
                                        <ImageWithPlaceholder src="https://via.placeholder.com/286x403/000000/ccc" alt="서브 배너" />
                                        <div className="hero-text">
                                            <h3>{displayCategories[0]?.name || '첫번째 카테고리'}</h3>
                                            <p>{displayCategories[0]?.description || '카테고리 설명'}</p>
                                        </div>
                                    </>
                                );
                            })()}
                        </div>
                        <div className={`hero-side-item top-right ${getBannerByPosition('세번째') ? 'has-banner-image' : ''}`}>
                            {(() => {
                                const thirdBanner = getBannerByPosition('세번째');
                                return thirdBanner ? (
                                    <img 
                                        src={thirdBanner.imageUrl} 
                                        alt="세번째 배너"
                                        style={{ 
                                            width: '100%', 
                                            height: '100%', 
                                            objectFit: 'contain',
                                            cursor: thirdBanner.linkUrl ? 'pointer' : 'default'
                                        }}
                                        onClick={() => handleBannerClick(thirdBanner)}
                                    />
                                ) : (
                                    <>
                                        <ImageWithPlaceholder src="https://via.placeholder.com/242x194/f0f0f0/ccc" alt="세번째 배너" />
                                        <div className="hero-text">
                                            <h4>{displayCategories[1]?.name || '두번째 카테고리'}</h4>
                                            <p>{displayCategories[1]?.description || '카테고리 설명'}</p>
                                        </div>
                                    </>
                                );
                            })()}
                        </div>
                        <div className={`hero-side-item bottom-right ${getBannerByPosition('네번째') ? 'has-banner-image' : ''}`}>
                            {(() => {
                                const fourthBanner = getBannerByPosition('네번째');
                                return fourthBanner ? (
                                    <img 
                                        src={fourthBanner.imageUrl} 
                                        alt="네번째 배너"
                                        style={{ 
                                            width: '100%', 
                                            height: '100%', 
                                            objectFit: 'contain',
                                            cursor: fourthBanner.linkUrl ? 'pointer' : 'default'
                                        }}
                                        onClick={() => handleBannerClick(fourthBanner)}
                                    />
                                ) : (
                                    <>
                                        <div className="hero-text">
                                            <h5>여기에 카탈로그 다운 or 블로그 연결?</h5>
                                        </div>
                                    </>
                                );
                            })()}
                        </div>
                    </div>
                </div>

                <div className="features-section">
                    <div className="feature">
                        <h4><span className="feature-title-default">신속 정확</span><span className="feature-title-primary">빠른 납기</span></h4>
                        <p>체계적인 생산관리</p>
                    </div>
                    <img src="/feature-divider.svg" alt="" className="feature-divider" aria-hidden />
                    <div className="feature">
                        <h4><span className="feature-title-primary">품질</span><span className="feature-title-default">최우선</span></h4>
                        <p>국내 생산 및 가공, 자체설비</p>
                    </div>
                    <img src="/feature-divider.svg" alt="" className="feature-divider" aria-hidden />
                    <div className="feature">
                        <h4><span className="feature-title-primary">신뢰</span><span className="feature-title-default">와 협력</span></h4>
                        <p>30년 이상의 비즈니스 경력</p>
                    </div>
                    <img src="/feature-divider.svg" alt="" className="feature-divider" aria-hidden />
                    <div className="feature">
                        <h4><span className="feature-title-default">고객</span><span className="feature-title-primary">맞춤 솔루션</span></h4>
                        <p>고객요구에 맞춘 최적의 시스템</p>
                    </div>
                </div>

                {/* 동적 카테고리 섹션들 - 처음 2개 카테고리를 개별 섹션으로 표시 */}
                {displayCategories.slice(0, 2).map((category, index) => (
                    <CategoryProductSection 
                        key={category.id || index}
                        category={category}
                        onProductClick={handleProductClick}
                    />
                ))}
                
                {/* 탭 섹션 - 모선부스바 → 마그네트 부스바 → 분전반 부스바 → 기타자재 순 */}
                <MainTabSection 
                    categories={tabCategoriesOrdered} 
                    activeTab={activeTab} // 탭 상태 관리
                    setActiveTab={setActiveTab} // 탭 상태 변경 함수
                    onProductClick={handleProductClick} 
                />

                 {/* 항상 블로그 섹션 표시 */}
                 <div className="blog-section">
                     <div className="blog-content">
                         <div className="blog-text-content">
                             <h2>부스바 월드의<br />제작 과정 부터 납기까지<br />생생하게 담은 블로그</h2>
                             <button className="blog-arrow-btn"></button>
                         </div>
                         <div className="blog-image-content">
                             {bottomBanner ? (
                                 <img 
                                     src={bottomBanner.imageUrl} 
                                     alt="하단 배너"
                                    style={{ 
                                        width: '100%', 
                                        height: '100%', 
                                        objectFit: 'cover',
                                        cursor: bottomBanner.linkUrl ? 'pointer' : 'default'
                                    }}
                                     onClick={() => handleBannerClick(bottomBanner)}
                                 />
                                                         ) : (
                                <ImageWithPlaceholder src={null} alt="Blog post" />
                            )}
                             <div className="blog-badge">
                                 <div className="frame-1707482016">
                                     <div className="blog-text">부스바 월드 BLOG</div>
                                 </div>
                                 <div className="frame-1707482014">
                                     <img className="mask-group" src="/mask-group0.svg" alt="Naver Blog" />
                                 </div>
                             </div>
                         </div>
                     </div>
                 </div>
            </main>
        </div>
    );
};

const HomePage = () => {
    return (
        <HomePageContent />
    );
};

export default HomePage; 